import {
  QUOTA_EXCEEDED_MESSAGE,
  apiForm,
  apiJson,
  apiStream,
  apiUrl,
  isQuotaExceededError,
  isRateLimitError,
  RATE_LIMIT_MESSAGE,
} from '../../../services/apiClient';
import { isPersonaFeatureBlockedError, INDIVIDUAL_TRIAL_ENDED_MESSAGE } from '../../../utils/productAccess';

const isDevelopment = () => process.env.NODE_ENV !== 'production';

export const formatPersonaErrorDetails = (details) => {
  if (!details) return '';
  if (Array.isArray(details)) {
    return details.filter(Boolean).map(String).join('\n');
  }
  if (typeof details === 'object') {
    return Object.entries(details)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
      .join('\n');
  }
  return String(details);
};

export const resolvePersonaApiErrorMessage = (error, fallback = '요청 처리에 실패했습니다.') => {
  if (isPersonaFeatureBlockedError(error)) {
    return '기업 전용 기능입니다. 무료 체험은 곧 제공될 예정입니다.';
  }
  if (isQuotaExceededError(error)) {
    if (error?.data?.trial_ended) {
      return INDIVIDUAL_TRIAL_ENDED_MESSAGE;
    }
    return error?.message || QUOTA_EXCEEDED_MESSAGE;
  }
  if (isRateLimitError(error)) {
    return RATE_LIMIT_MESSAGE;
  }
  if (error?.data) {
    return personaApiErrorMessage(error.data, error.message || fallback);
  }
  return error?.message || fallback;
};

const personaApiErrorMessage = (response, fallback = '요청 처리에 실패했습니다.') => {
  if (response?.error === 'quota_exceeded' || response?.status === 402) {
    if (response?.trial_ended) {
      return INDIVIDUAL_TRIAL_ENDED_MESSAGE;
    }
    return response?.message || QUOTA_EXCEEDED_MESSAGE;
  }
  if (response?.error === 'rate_limit_exceeded' || response?.status === 429) {
    return RATE_LIMIT_MESSAGE;
  }
  const message = response?.message || response?.error || response?.msg || fallback;
  const details = formatPersonaErrorDetails(response?.details);
  return details ? `${message}\n${details}` : message;
};

export const normalizeGenerationError = (error) => {
  if (isQuotaExceededError(error)) {
    return new Error(QUOTA_EXCEEDED_MESSAGE);
  }
  if (error?.data) {
    return new Error(personaApiErrorMessage(error.data, error.message || 'AI persona 생성에 실패했습니다.'));
  }
  return error instanceof Error ? error : new Error('AI persona 생성에 실패했습니다.');
};

export const normalizeGenerationResponse = (response) => {
  if (!response) {
    throw new Error('Persona preview response is empty.');
  }
  if (response.success === false) {
    throw new Error(personaApiErrorMessage(response, 'AI persona 생성에 실패했습니다.'));
  }

  let normalized = response;
  if (!Array.isArray(response.personas) && Array.isArray(response.data?.personas)) {
    if (isDevelopment()) {
      console.warn('[persona] Legacy data.personas preview response shape received. Prefer top-level personas.');
    }
    normalized = {
      ...response.data,
      success: response.success,
    };
  }

  if (!Array.isArray(normalized.personas)) {
    throw new Error('Persona preview response is missing personas array.');
  }

  return {
    ...normalized,
    personas: normalized.personas,
    segments: Array.isArray(normalized.segments) ? normalized.segments : [],
    generationMetadata: normalized.generationMetadata || {},
    tokenUsage: normalized.tokenUsage || {},
    telecomServiceUsageContextReferences: Array.isArray(normalized.telecomServiceUsageContextReferences)
      ? normalized.telecomServiceUsageContextReferences
      : [],
  };
};

const unwrap = (response, fallback = null) => {
  if (!response) return fallback;
  if (response.success === false) {
    throw new Error(personaApiErrorMessage(response));
  }
  if (Object.prototype.hasOwnProperty.call(response, 'persona')) {
    return response.persona;
  }
  if (Object.prototype.hasOwnProperty.call(response, 'data')) {
    return response.data;
  }
  return response;
};

export const unwrapPersonaResponseForTest = unwrap;

export const normalizeTestSummariesResponse = (response) => {
  if (!response) return { items: [], pagination: {} };
  if (response.success === false) {
    throw new Error(personaApiErrorMessage(response));
  }

  return {
    items: Array.isArray(response.data) ? response.data : [],
    pagination: response.pagination || {},
  };
};

const withQuery = (path, params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

const jsonOptions = (method, payload) => ({
  method,
  body: JSON.stringify(payload || {}),
});

const stringList = (value) => (
  Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : []
);

const normalizeInterviewQuestionSet = (questionSet = {}) => ({
  opening: stringList(questionSet.opening),
  tasks: (Array.isArray(questionSet.tasks) ? questionSet.tasks : []).map((task, index) => ({
    title: String(task?.title || `영역${index + 1}`).trim(),
    questions: stringList(task?.questions),
  })).filter((task) => task.questions.length > 0),
  closing: stringList(questionSet.closing),
  followup_strategies: stringList(questionSet.followup_strategies || questionSet.followUps),
});

export const interviewQuestionSetToDraft = (questionSet = {}) => {
  const normalized = normalizeInterviewQuestionSet(questionSet);
  return {
    opening: normalized.opening,
    taskGroups: normalized.tasks.map((task, index) => ({
      id: `task-${index + 1}`,
      title: task.title || `영역${index + 1}`,
      questions: task.questions,
    })),
    closing: normalized.closing,
    followUps: normalized.followup_strategies,
  };
};

export const interviewDraftToQuestionSet = (draft = {}) => ({
  opening: stringList(draft.opening),
  tasks: (Array.isArray(draft.taskGroups) ? draft.taskGroups : []).map((group, index) => ({
    title: String(group?.title || `영역${index + 1}`).trim(),
    questions: stringList(group?.questions),
  })).filter((group) => group.questions.length > 0),
  closing: stringList(draft.closing),
  followup_strategies: stringList(draft.followUps),
});

export const normalizeList = (response) => ({
  items: Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [],
  pagination: response?.pagination || null,
});

const normalizeFolders = (response) => {
  if (Array.isArray(response?.folders)) return response.folders;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

const unwrapPersona = (response) => {
  if (!response) return null;
  if (response.success === false) {
    throw new Error(personaApiErrorMessage(response));
  }
  if (response.persona) return response.persona;
  return unwrap(response);
};

const unwrapPersonas = (response) => {
  if (!response) return [];
  if (response.success === false) {
    throw new Error(personaApiErrorMessage(response));
  }
  if (Array.isArray(response.personas)) return response.personas;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
};

export const personaAssetUrl = (value) => {
  if (!value) return '';
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  return apiUrl(value);
};

export const personaResultShareApi = {
  async createShare(resourceType, resourceId) {
    return unwrap(await apiJson('/persona/share-links', jsonOptions('POST', { resourceType, resourceId })));
  },

  async getShare(token) {
    return unwrap(await apiJson(`/persona/share-links/${encodeURIComponent(token)}`, { authHeaders: false }));
  },

  async revokeShare(token) {
    return apiJson(`/persona/share-links/${encodeURIComponent(token)}`, { method: 'DELETE' });
  },
};

export const normalizeInterviewPersona = (persona = {}) => {
  const folderId = persona.folderId ?? persona.folder_id ?? 'ungrouped';
  const profile = persona.profile && typeof persona.profile === 'object' ? persona.profile : {};
  const folder = persona.folder && typeof persona.folder === 'object' ? persona.folder : {};
  return {
    ...persona,
    id: persona.id,
    name: persona.name || '이름 없음',
    gender: persona.gender || '-',
    age: persona.age ? `${persona.age}세` : '-',
    role: persona.title || persona.roleArea || persona.role_area || '-',
    personality: persona.personality || profile.personality || '-',
    behavior: persona.behaviours || persona.behavior_pattern || profile.behaviours || profile.behavior || '-',
    imageUrl: personaAssetUrl(persona.imageUrl || persona.image_url || ''),
    folderId: folderId || 'ungrouped',
    folderName: persona.folderName || persona.folder_name || folder.name || (folderId ? `폴더 ${folderId}` : '그룹없음'),
    folderCreatedAt: persona.folderCreatedAt || persona.folder_created_at || folder.createdAt || folder.created_at || null,
    raw: persona,
  };
};

const folderCreatedSortValue = (folder) => {
  const timestamp = Date.parse(folder?.created_at || folder?.createdAt || folder?.folderCreatedAt || '');
  if (Number.isFinite(timestamp)) return timestamp;
  const idValue = Number(folder?.id);
  return Number.isFinite(idValue) ? idValue : 0;
};

const folderUpdatedSortValue = (folder) => {
  const timestamp = Date.parse(
    folder?.updated_at ||
    folder?.updatedAt ||
    folder?.created_at ||
    folder?.createdAt ||
    folder?.folderCreatedAt ||
    '',
  );
  if (Number.isFinite(timestamp)) return timestamp;
  const idValue = Number(folder?.id);
  return Number.isFinite(idValue) ? idValue : 0;
};

export const isDefaultPersonaFolder = (folder) => Boolean(folder?.is_default ?? folder?.isDefault);

export const sortPersonaFolders = (folders = []) => [...folders].sort((left, right) => {
  const defaultOrder = Number(isDefaultPersonaFolder(right)) - Number(isDefaultPersonaFolder(left));
  if (defaultOrder !== 0) return defaultOrder;
  return folderUpdatedSortValue(right) - folderUpdatedSortValue(left);
});

export const groupInterviewPersonas = (personas = [], folders = []) => {
  const normalizedPersonas = personas.map(normalizeInterviewPersona);
  if (Array.isArray(folders) && folders.length > 0) {
    const sortedFolders = sortPersonaFolders(folders);
    const folderGroups = sortedFolders.map((folder) => {
      const folderId = String(folder.id);
      return {
        id: folderId,
        name: folder.name || `폴더 ${folderId}`,
        personas: normalizedPersonas.filter((persona) => String(persona.folderId) === folderId),
      };
    }).filter((group) => group.personas.length > 0);
    const groupedIds = new Set(folderGroups.flatMap((group) => group.personas.map((persona) => String(persona.id))));
    const ungrouped = normalizedPersonas.filter((persona) => !groupedIds.has(String(persona.id)));
    if (ungrouped.length > 0) {
      folderGroups.push({
        id: 'ungrouped',
        name: '그룹없음',
        personas: ungrouped,
      });
    }
    return folderGroups;
  }

  const groupMap = new Map();
  normalizedPersonas.forEach((persona) => {
    const key = persona.folderId || 'ungrouped';
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        id: key,
        name: key === 'ungrouped' ? '그룹없음' : persona.folderName || `폴더 ${key}`,
        folderCreatedAt: persona.folderCreatedAt,
        personas: [],
      });
    }
    groupMap.get(key).personas.push(persona);
  });
  return Array.from(groupMap.values()).sort((left, right) => {
    if (left.id === 'ungrouped') return 1;
    if (right.id === 'ungrouped') return -1;
    return folderCreatedSortValue(right) - folderCreatedSortValue(left);
  });
};

export const normalizeInterviewResult = (result = {}) => {
  const personaSnapshot = result.personaSnapshot || result.persona_snapshot || result.persona || null;
  const personaId = result.personaId ?? result.persona_id ?? personaSnapshot?.id ?? null;
  return {
    ...result,
    id: result.id,
    interviewId: result.interviewId ?? result.interview_id,
    personaId,
    personaName: result.personaName || result.persona_name || personaSnapshot?.name || '퍼소나',
    personaSnapshot: personaSnapshot ? normalizeInterviewPersona(personaSnapshot) : null,
    status: result.status || 'completed',
    summary: result.summary && typeof result.summary === 'object' ? result.summary : {},
    turns: Array.isArray(result.turns) ? result.turns : [],
    errorMessage: result.errorMessage || result.error_message || result.error || '',
    createdAt: result.createdAt || result.created_at,
    updatedAt: result.updatedAt || result.updated_at,
  };
};

export const normalizeInterview = (interview = {}) => {
  const questionSet = interview.questionSet || interview.question_set || interview.questions || {};
  const results = Array.isArray(interview.results) ? interview.results : [];
  const summary = interview.summary || {};
  const insights = interview.insights
    || summary.insightReport
    || summary.insight_report
    || null;
  return {
    ...interview,
    productDescription: interview.productDescription || interview.product_description || '',
    questionSet: normalizeInterviewQuestionSet(questionSet),
    personaIds: interview.personaIds || interview.persona_ids || [],
    results: results.map(normalizeInterviewResult),
    summary,
    insights,
    errorMessage: interview.errorMessage || interview.error_message || interview.error || '',
    createdAt: interview.createdAt || interview.created_at,
    updatedAt: interview.updatedAt || interview.updated_at,
    completedAt: interview.completedAt || interview.completed_at,
  };
};

const unwrapInterview = (response) => {
  if (!response) return null;
  if (response.success === false) {
    throw new Error(personaApiErrorMessage(response));
  }
  const payload = Object.prototype.hasOwnProperty.call(response, 'data') ? response.data : response;
  const results = Array.isArray(response.results) ? response.results : payload?.results;
  return normalizeInterview({ ...payload, results });
};

export const personaApi = {
  async listFolders() {
    return normalizeFolders(await apiJson('/persona/folders'));
  },

  async createFolder(payload) {
    return unwrap(await apiJson('/persona/folders', jsonOptions('POST', payload)));
  },

  async updateFolder(folderId, payload) {
    return unwrap(await apiJson(`/persona/folders/${folderId}`, jsonOptions('PATCH', payload)));
  },

  async deleteFolder(folderId) {
    return apiJson(`/persona/folders/${folderId}`, { method: 'DELETE' });
  },

  async listPersonas(params = {}) {
    const response = await apiJson(withQuery('/persona/personas', params));
    return normalizeList(response);
  },

  async listExplorePersonas(params = {}) {
    const response = await apiJson(withQuery('/persona/explore/personas', {
      page: params.page || 1,
      limit: params.limit || 6,
      search: params.search || undefined,
      tag: params.tag || undefined,
    }));
    if (response?.success === false) {
      throw new Error(personaApiErrorMessage(response));
    }
    return {
      data: Array.isArray(response?.data) ? response.data : [],
      catalogSync: response?.catalogSync || {},
      facets: response?.facets || { tags: [] },
      pagination: response?.pagination || null,
    };
  },

  async getExplorePersona(catalogId) {
    return unwrap(await apiJson(`/persona/explore/personas/${catalogId}`));
  },

  async getPersona(personaId) {
    return unwrap(await apiJson(`/persona/personas/${personaId}`));
  },

  async adoptExplorePersona(catalogId) {
    const response = await apiJson(`/persona/explore/personas/${catalogId}/adopt`, { method: 'POST' });
    if (response?.success === false) {
      throw new Error(personaApiErrorMessage(response, '퍼소나 담기에 실패했습니다.'));
    }
    return {
      persona: response?.persona || null,
      alreadyAdopted: Boolean(response?.alreadyAdopted),
      sourceCatalogId: response?.sourceCatalogId ?? response?.source_catalog_id ?? null,
    };
  },

  async createPersona(payload) {
    return unwrapPersona(await apiJson('/persona/personas/manual', jsonOptions('POST', payload)));
  },

  async generatePersonas(payload) {
    try {
      return normalizeGenerationResponse(await apiJson('/persona/personas', jsonOptions('POST', payload)));
    } catch (error) {
      throw normalizeGenerationError(error);
    }
  },

  async generateSegments(payload) {
    try {
      const response = await apiJson('/persona/personas/segments', jsonOptions('POST', payload));
      if (response?.success === false) {
        throw new Error(personaApiErrorMessage(response, '세그먼트 추천에 실패했습니다.'));
      }
      if (!Array.isArray(response?.segments)) {
        throw new Error('Segment suggestion response is missing segments array.');
      }
      return response;
    } catch (error) {
      if (error?.data) {
        throw new Error(personaApiErrorMessage(error.data, error.message || '세그먼트 추천에 실패했습니다.'));
      }
      throw error;
    }
  },

  async saveGeneratedPersonas(payload) {
    return unwrapPersonas(await apiJson('/persona/personas/save', jsonOptions('POST', payload)));
  },

  async updatePersona(personaId, payload) {
    return unwrap(await apiJson(`/persona/personas/${personaId}`, jsonOptions('PATCH', payload)));
  },

  async deletePersona(personaId) {
    return apiJson(`/persona/personas/${personaId}`, { method: 'DELETE' });
  },

  async getMemory(personaId) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/memory`), {});
  },

  async updateMemorySettings(personaId, payload) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/memory/settings`, jsonOptions('PATCH', payload)));
  },

  async addActivity(personaId, payload) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/activities`, jsonOptions('POST', payload)));
  },

  async addTrait(personaId, payload) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/learned-traits`, jsonOptions('POST', payload)));
  },

  async deleteTrait(personaId, traitId) {
    return apiJson(`/persona/personas/${personaId}/learned-traits/${traitId}`, { method: 'DELETE' });
  },

  async uploadAsset(file, assetType = 'upload') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', assetType);
    const response = await apiForm('/persona/storage/upload', formData);
    const data = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    if (!response.ok || data?.success === false) {
      throw new Error(personaApiErrorMessage(data, '이미지 업로드에 실패했습니다.'));
    }
    return unwrap(data);
  },

  async attachPersonaImage(personaId, payload) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/image`, jsonOptions('POST', payload)));
  },
};

export const personaChatApi = {
  async listSessions(personaId) {
    const rows = unwrap(await apiJson(`/persona/personas/${personaId}/chat/sessions`), []);
    return Array.isArray(rows) ? rows : [];
  },

  async createSession(personaId, payload = {}) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/chat/sessions`, jsonOptions('POST', payload)));
  },

  async getOrCreateSession(personaId) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/chat/sessions/latest`));
  },

  async getSession(personaId, sessionId) {
    return unwrap(await apiJson(`/persona/personas/${personaId}/chat/sessions/${sessionId}`));
  },

  async sendMessage(personaId, sessionId, payload = {}) {
    const body = {
      text: payload.text ?? payload.content ?? '',
      imageAssetId: payload.imageAssetId ?? payload.image_asset_id ?? null,
    };
    return unwrap(
      await apiJson(
        `/persona/personas/${personaId}/chat/sessions/${sessionId}/messages`,
        jsonOptions('POST', body),
      ),
    );
  },

  async streamMessage(personaId, sessionId, payload = {}) {
    const body = {
      text: payload.text ?? payload.content ?? '',
      imageAssetId: payload.imageAssetId ?? payload.image_asset_id ?? null,
    };
    return apiStream(`/persona/personas/${personaId}/chat/sessions/${sessionId}/messages/stream`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

export const personaTestApi = {
  async listSummaries({ page = 1, pageSize = 10 } = {}) {
    const response = await apiJson(withQuery('/persona/tests/list', { page, page_size: pageSize }));
    return normalizeTestSummariesResponse(response);
  },

  async listTests() {
    return unwrap(await apiJson('/persona/tests'), []);
  },

  async generateContextFromPlan(payload) {
    const response = await apiJson('/persona/tests/context/from-plan', jsonOptions('POST', payload));
    const data = unwrap(response, {});
    return data?.context || data;
  },

  async createTest(payload) {
    return unwrap(await apiJson('/persona/tests', jsonOptions('POST', payload)));
  },

  async getTest(testId) {
    return unwrap(await apiJson(`/persona/tests/${testId}`));
  },

  async updateTest(testId, payload) {
    return unwrap(await apiJson(`/persona/tests/${testId}`, jsonOptions('PATCH', payload)));
  },

  async deleteTest(testId) {
    return apiJson(`/persona/tests/${testId}`, { method: 'DELETE' });
  },

  async runTest(testId, payload = {}) {
    return apiJson(`/persona/tests/${testId}/run`, jsonOptions('POST', payload));
  },

  async listResults(testId) {
    return unwrap(await apiJson(`/persona/tests/${testId}/results`), []);
  },

  async captureUrl(url) {
    return unwrap(await apiJson('/persona/tests/capture-url', jsonOptions('POST', { url })));
  },

  async generateInsights(testId, { scopeType, scopeIndex = null, force = false } = {}) {
    return unwrap(await apiJson(
      `/persona/tests/${testId}/insights`,
      jsonOptions('POST', { scopeType, scopeIndex, force }),
    ));
  },
};

export const personaAbTestApi = {
  async listTests() {
    return unwrap(await apiJson('/persona/ab-tests'), []);
  },

  async createTest(payload) {
    return unwrap(await apiJson('/persona/ab-tests', jsonOptions('POST', payload)));
  },

  async getTest(testId) {
    return unwrap(await apiJson(`/persona/ab-tests/${testId}`));
  },

  async deleteTest(testId) {
    return apiJson(`/persona/ab-tests/${testId}`, { method: 'DELETE' });
  },

  async runTest(testId, payload = {}) {
    return apiJson(`/persona/ab-tests/${testId}/run`, jsonOptions('POST', payload));
  },

  async listResults(testId) {
    return unwrap(await apiJson(`/persona/ab-tests/${testId}/results`), []);
  },
};

export const personaInterviewApi = {
  async generateQuestions(payload) {
    const response = await apiJson('/persona/interviews/questions', jsonOptions('POST', payload));
    const data = unwrap(response, {});
    return normalizeInterviewQuestionSet(data?.questions || data?.questionSet || data);
  },

  async generateQuestionsFromGuideline(payload) {
    const response = await apiJson('/persona/interviews/questions/from-guideline', jsonOptions('POST', payload));
    const data = unwrap(response, {});
    return normalizeInterviewQuestionSet(data?.questions || data?.questionSet || data);
  },

  async generateContextFromPlan(payload) {
    const response = await apiJson('/persona/interviews/context/from-plan', jsonOptions('POST', payload));
    const data = unwrap(response, {});
    return data?.context || data;
  },

  async createInterview(payload) {
    return unwrapInterview(await apiJson('/persona/interviews', jsonOptions('POST', payload)));
  },

  async listInterviews() {
    const rows = unwrap(await apiJson('/persona/interviews'), []);
    return Array.isArray(rows) ? rows.map(normalizeInterview) : [];
  },

  async getInterview(interviewId) {
    return unwrapInterview(await apiJson(`/persona/interviews/${interviewId}`));
  },

  async deleteInterview(interviewId) {
    return apiJson(`/persona/interviews/${interviewId}`, { method: 'DELETE' });
  },

  async runInterview(interviewId, payload = {}) {
    return unwrapInterview(await apiJson(`/persona/interviews/${interviewId}/run`, jsonOptions('POST', payload)));
  },

  async generateInsights(interviewId, payload = {}) {
    const response = await apiJson(
      `/persona/interviews/${interviewId}/insights`,
      jsonOptions('POST', payload),
    );
    if (response?.success === false) {
      throw new Error(personaApiErrorMessage(response));
    }
    const data = unwrap(response, {});
    return {
      insight: data?.insight || null,
      interview: data?.interview ? normalizeInterview(data.interview) : null,
      cached: Boolean(data?.cached),
    };
  },

  async purposeRun(payload) {
    const response = await apiJson('/persona/interviews/purpose-run', jsonOptions('POST', payload));
    if (response?.success === false) {
      throw new Error(personaApiErrorMessage(response));
    }
    const data = unwrap(response, {});
    return {
      interview: data?.interview ? normalizeInterview(data.interview) : null,
      insight: data?.insight || null,
      insightError: data?.insightError || data?.insight_error || null,
    };
  },

  async listPersonas() {
    const rows = unwrap(await apiJson('/persona/interviews/personas'), []);
    return Array.isArray(rows) ? rows.map(normalizeInterviewPersona) : [];
  },
};

export const personaFigmaApi = {
  async getStatus() {
    return apiJson('/persona/figma/status');
  },

  async getConnectUrl() {
    const response = await apiJson('/persona/figma/connect');
    return response.url || response.data?.url;
  },

  async disconnect() {
    return apiJson('/persona/figma/disconnect', { method: 'DELETE' });
  },

  async listFiles() {
    return unwrap(await apiJson('/persona/figma/files'), []);
  },

  async syncFile(payload) {
    return unwrap(await apiJson('/persona/figma/files/sync', jsonOptions('POST', payload)));
  },

  async refreshFile(fileId) {
    return unwrap(await apiJson(`/persona/figma/files/${fileId}/sync`, jsonOptions('POST')));
  },

  async deleteFile(fileId) {
    return apiJson(`/persona/figma/files/${fileId}`, { method: 'DELETE' });
  },

  async listFlows(fileId) {
    return unwrap(await apiJson(`/persona/figma/files/${fileId}/flows`), []);
  },

  async previewFlow(fileId, flowId) {
    return unwrap(await apiJson(`/persona/figma/files/${fileId}/flows/${flowId}/preview`), {});
  },

  async syncFlows(fileId, flows) {
    return unwrap(await apiJson(`/persona/figma/files/${fileId}/flows/sync`, jsonOptions('POST', { flows })), []);
  },
};
